import {
  getGifEditAppearanceColorMatrix,
  isIdentityGifEditAppearance,
} from '@buffer-mono/gif-toolkit'

import { GIF_EDIT_OPAQUE_ALPHA_MIN } from './limits'
import type { GifEditRecipe, GifEditRotation } from './recipe'

export type GifDimensions = Readonly<{
  width: number
  height: number
}>

type TransformMatrix = Readonly<{
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}>

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value))

const roundPixelValue = (value: number): number =>
  Math.round(value + Number.EPSILON * Math.max(1, Math.abs(value)) * 4)

const getQuarterTurn = (
  source: GifDimensions,
  rotation: GifEditRotation,
): Readonly<{ matrix: TransformMatrix; stage: GifDimensions }> => {
  const { width, height } = source

  switch (rotation) {
    case 0:
      return {
        matrix: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
        stage: { width, height },
      }
    case 90:
      return {
        matrix: { a: 0, b: 1, c: -1, d: 0, e: height, f: 0 },
        stage: { width: height, height: width },
      }
    case 180:
      return {
        matrix: { a: -1, b: 0, c: 0, d: -1, e: width, f: height },
        stage: { width, height },
      }
    case 270:
      return {
        matrix: { a: 0, b: -1, c: 1, d: 0, e: 0, f: width },
        stage: { width: height, height: width },
      }
  }
}

const compose = (
  outer: TransformMatrix,
  inner: TransformMatrix,
): TransformMatrix => ({
  a: outer.a * inner.a + outer.c * inner.b,
  b: outer.b * inner.a + outer.d * inner.b,
  c: outer.a * inner.c + outer.c * inner.d,
  d: outer.b * inner.c + outer.d * inner.d,
  e: outer.a * inner.e + outer.c * inner.f + outer.e,
  f: outer.b * inner.e + outer.d * inner.f + outer.f,
})

const invert = (transform: TransformMatrix): TransformMatrix => {
  const determinant = transform.a * transform.d - transform.b * transform.c
  return {
    a: transform.d / determinant,
    b: -transform.b / determinant,
    c: -transform.c / determinant,
    d: transform.a / determinant,
    e: (transform.c * transform.f - transform.d * transform.e) / determinant,
    f: (transform.b * transform.e - transform.a * transform.f) / determinant,
  }
}

const getCoverScale = (stage: GifDimensions, radians: number): number => {
  const cosine = Math.abs(Math.cos(radians))
  const sine = Math.abs(Math.sin(radians))
  return Math.max(
    cosine + (stage.height / stage.width) * sine,
    cosine + (stage.width / stage.height) * sine,
  )
}

const getOrientedToStage = (
  stage: GifDimensions,
  recipe: GifEditRecipe,
  radians: number,
  coverScale: number,
): TransformMatrix => {
  const horizontalDirection = recipe.flipHorizontal ? -1 : 1
  const verticalDirection = recipe.flipVertical ? -1 : 1
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  const a = horizontalDirection * coverScale * cosine
  const b = verticalDirection * coverScale * sine
  const c = -horizontalDirection * coverScale * sine
  const d = verticalDirection * coverScale * cosine
  const centerX = stage.width / 2
  const centerY = stage.height / 2

  return {
    a,
    b,
    c,
    d,
    e: centerX - a * centerX - c * centerY,
    f: centerY - b * centerX - d * centerY,
  }
}

export const getGifEditOutputGeometry = (
  source: GifDimensions,
  recipe: GifEditRecipe,
): Readonly<{
  width: number
  height: number
  cropLeft: number
  cropTop: number
  stageToSource: TransformMatrix
}> => {
  const quarterTurn = getQuarterTurn(source, recipe.rotation)
  const radians = (recipe.straightening * Math.PI) / 180
  const coverScale = getCoverScale(quarterTurn.stage, radians)
  const sourceToStage = compose(
    getOrientedToStage(quarterTurn.stage, recipe, radians, coverScale),
    quarterTurn.matrix,
  )
  const imageWidth = Math.max(1, Math.round(quarterTurn.stage.width))
  const imageHeight = Math.max(1, Math.round(quarterTurn.stage.height))
  const width = clamp(
    roundPixelValue(recipe.crop.width * imageWidth),
    1,
    imageWidth,
  )
  const height = clamp(
    roundPixelValue(recipe.crop.height * imageHeight),
    1,
    imageHeight,
  )
  const cropLeft = clamp(
    roundPixelValue(recipe.crop.x * imageWidth),
    0,
    imageWidth - width,
  )
  const cropTop = clamp(
    roundPixelValue(recipe.crop.y * imageHeight),
    0,
    imageHeight - height,
  )

  return {
    width,
    height,
    cropLeft,
    cropTop,
    stageToSource: invert(sourceToStage),
  }
}

const samplePixel = (
  source: Uint8ClampedArray,
  sourceWidth: number,
  sourceHeight: number,
  pixelX: number,
  pixelY: number,
): readonly [number, number, number, number] => {
  if (
    pixelX < 0 ||
    pixelY < 0 ||
    pixelX >= sourceWidth ||
    pixelY >= sourceHeight
  ) {
    return [0, 0, 0, 0]
  }
  const index = (pixelY * sourceWidth + pixelX) * 4
  return [
    source[index] ?? 0,
    source[index + 1] ?? 0,
    source[index + 2] ?? 0,
    source[index + 3] ?? 0,
  ]
}

const sampleNearest = (
  source: Uint8ClampedArray,
  sourceWidth: number,
  sourceHeight: number,
  x: number,
  y: number,
): readonly [number, number, number, number] =>
  samplePixel(source, sourceWidth, sourceHeight, Math.floor(x), Math.floor(y))

const mixPremultiplied = (
  first: readonly [number, number, number, number],
  second: readonly [number, number, number, number],
  amount: number,
): readonly [number, number, number, number] => {
  const firstAlpha = first[3]
  const secondAlpha = second[3]
  const red =
    first[0] * firstAlpha * (1 - amount) + second[0] * secondAlpha * amount
  const green =
    first[1] * firstAlpha * (1 - amount) + second[1] * secondAlpha * amount
  const blue =
    first[2] * firstAlpha * (1 - amount) + second[2] * secondAlpha * amount
  const alpha = firstAlpha * (1 - amount) + secondAlpha * amount
  if (alpha <= 0) return [0, 0, 0, 0]
  return [red / alpha, green / alpha, blue / alpha, alpha]
}

const sampleBilinear = (
  source: Uint8ClampedArray,
  sourceWidth: number,
  sourceHeight: number,
  x: number,
  y: number,
): readonly [number, number, number, number] => {
  const u = x - 0.5
  const v = y - 0.5
  const x0 = Math.floor(u)
  const y0 = Math.floor(v)
  const x1 = x0 + 1
  const y1 = y0 + 1
  const tx = u - x0
  const ty = v - y0
  const topLeft = samplePixel(source, sourceWidth, sourceHeight, x0, y0)
  const topRight = samplePixel(source, sourceWidth, sourceHeight, x1, y0)
  const bottomLeft = samplePixel(source, sourceWidth, sourceHeight, x0, y1)
  const bottomRight = samplePixel(source, sourceWidth, sourceHeight, x1, y1)

  return mixPremultiplied(
    mixPremultiplied(topLeft, topRight, tx),
    mixPremultiplied(bottomLeft, bottomRight, tx),
    ty,
  )
}

const applyAppearance = (
  pixels: Uint8ClampedArray,
  recipe: GifEditRecipe,
): void => {
  if (isIdentityGifEditAppearance(recipe)) return

  const matrix = getGifEditAppearanceColorMatrix(recipe)
  for (let offset = 0; offset < pixels.length; offset += 4) {
    const red = pixels[offset] ?? 0
    const green = pixels[offset + 1] ?? 0
    const blue = pixels[offset + 2] ?? 0
    const alpha = pixels[offset + 3] ?? 0
    pixels[offset] = clamp(
      matrix[0][0] * red +
        matrix[0][1] * green +
        matrix[0][2] * blue +
        matrix[0][3] * alpha +
        matrix[0][4],
      0,
      255,
    )
    pixels[offset + 1] = clamp(
      matrix[1][0] * red +
        matrix[1][1] * green +
        matrix[1][2] * blue +
        matrix[1][3] * alpha +
        matrix[1][4],
      0,
      255,
    )
    pixels[offset + 2] = clamp(
      matrix[2][0] * red +
        matrix[2][1] * green +
        matrix[2][2] * blue +
        matrix[2][3] * alpha +
        matrix[2][4],
      0,
      255,
    )
  }
}

const snapBinaryAlpha = (pixels: Uint8ClampedArray): void => {
  for (let offset = 0; offset < pixels.length; offset += 4) {
    const alpha = pixels[offset + 3] ?? 0
    if (alpha < GIF_EDIT_OPAQUE_ALPHA_MIN) {
      pixels[offset] = 0
      pixels[offset + 1] = 0
      pixels[offset + 2] = 0
      pixels[offset + 3] = 0
    } else {
      pixels[offset + 3] = 255
    }
  }
}

export const transformCompositedFrame = (
  source: Uint8ClampedArray,
  sourceDimensions: GifDimensions,
  recipe: GifEditRecipe,
  output: Uint8ClampedArray,
): GifDimensions => {
  const geometry = getGifEditOutputGeometry(sourceDimensions, recipe)
  const requiredLength = geometry.width * geometry.height * 4
  if (output.length !== requiredLength) {
    throw new RangeError(
      `output must contain exactly ${requiredLength} bytes for a ${geometry.width}x${geometry.height} frame`,
    )
  }
  const useBilinear = recipe.straightening !== 0

  for (let y = 0; y < geometry.height; y += 1) {
    for (let x = 0; x < geometry.width; x += 1) {
      const stageX = geometry.cropLeft + x + 0.5
      const stageY = geometry.cropTop + y + 0.5
      const sourceX =
        geometry.stageToSource.a * stageX +
        geometry.stageToSource.c * stageY +
        geometry.stageToSource.e
      const sourceY =
        geometry.stageToSource.b * stageX +
        geometry.stageToSource.d * stageY +
        geometry.stageToSource.f
      const sample = useBilinear
        ? sampleBilinear(
            source,
            sourceDimensions.width,
            sourceDimensions.height,
            sourceX,
            sourceY,
          )
        : sampleNearest(
            source,
            sourceDimensions.width,
            sourceDimensions.height,
            sourceX,
            sourceY,
          )
      const dest = (y * geometry.width + x) * 4
      output[dest] = sample[0]
      output[dest + 1] = sample[1]
      output[dest + 2] = sample[2]
      output[dest + 3] = sample[3]
    }
  }

  applyAppearance(output, recipe)
  snapBinaryAlpha(output)
  return { width: geometry.width, height: geometry.height }
}
